import {
	InterestedParty,
	LiveInformationProvider,
	SuccessfulCacheItem
} from "client_legacy/Workspace/LiveInformation/liveInformationProvider";
import {
	ZaaksysteemLiveInformationResponseModel,
	ZaaksysteemLiveInformationRequestModel,
	getLiveInformation,
	ZaaksysteemResultStatus,
	ZaaksysteemInterests,
	ZaaksysteemCaseListItemModel,
	ZaaksysteemResourceKind
} from "client/Api/Workspace/LiveInformation/Zaaksysteem/zaaksysteemLiveInformation";
import { Guid } from "client_legacy/ndaw/guid";
import { UnreachableCaseError } from "client_legacy/ndaw/errors";
import { NormalizedMap } from "client_legacy/ndaw/normalizedMap";

interface ZaaksysteemLiveInformationProviderOptions {
	intervalInSeconds: number;
}

interface ZaaksysteemResourceId {
	connectionId: number;
	resourceKind: ZaaksysteemResourceKind;
}

interface InterestedZaaksysteemParty extends InterestedParty<Guid, ZaaksysteemResourceId> {
	resourceKind: ZaaksysteemResourceKind;
	updateItems(items: ReadonlyArray<ZaaksysteemCaseListItemModel>, hasMore: boolean): void;
	updateCount(count: number): void;
	informAboutAccessViolation(): void;
}

class ZaaksysteemLiveInformationProvider extends LiveInformationProvider<
	ZaaksysteemLiveInformationProviderOptions,
	Guid,
	ZaaksysteemResourceId,
	ZaaksysteemInterests,
	InterestedZaaksysteemParty,
	ZaaksysteemLiveInformationResponseModel
> {

	constructor(options: ZaaksysteemLiveInformationProviderOptions) {
		super(options);
	}

	protected async getDataFromBackend(
		entries: ReadonlyArray<[ZaaksysteemResourceId, ZaaksysteemInterests]>
	): Promise<ReadonlyArray<[ZaaksysteemResourceId, ZaaksysteemLiveInformationResponseModel]>> {
		const requests: ZaaksysteemLiveInformationRequestModel[] = entries.map(entry => this.convertResourceIdToRequest(entry[0], entry[1]));
		const responses: ReadonlyArray<ZaaksysteemLiveInformationResponseModel> = await getLiveInformation(requests);

		return responses.map(response => [this.convertResponseToResourceId(response), response]);
	}

	protected updateCacheItem(
		cacheItem: SuccessfulCacheItem<ZaaksysteemInterests, ZaaksysteemLiveInformationResponseModel>,
		response: [ZaaksysteemInterests, ZaaksysteemLiveInformationResponseModel]
	) {
		const data: ZaaksysteemLiveInformationResponseModel = response[1];

		cacheItem.retrievedInterests.items = true;
		cacheItem.data = data;
	}

	protected informParty(party: InterestedZaaksysteemParty, cacheItem: [ZaaksysteemInterests, ZaaksysteemLiveInformationResponseModel]) {
		const response: ZaaksysteemLiveInformationResponseModel = cacheItem[1];

		switch (response.status) {
			case ZaaksysteemResultStatus.Success:
				const { itemsResponse, countResponse } = response;
				if (itemsResponse) {
					party.updateItems(itemsResponse.items, itemsResponse.hasMore);
				}
				if (countResponse) {
					party.updateCount(countResponse.count);
				}

				break;
			case ZaaksysteemResultStatus.NoAccess:
				party.informAboutAccessViolation();
				break;
			case ZaaksysteemResultStatus.GeneralFailure:
				party.informAboutGlobalRetrievalFailure();
				break;
			default:
				throw new UnreachableCaseError(response);
		}
	}

	protected override createResourceMap<TValue>(): Map<ZaaksysteemResourceId, TValue> {
		return new NormalizedMap<ZaaksysteemResourceId, TValue>(
			normalizeKey
		);
	}

	private convertResourceIdToRequest(resourceId: ZaaksysteemResourceId, interestedIn: ZaaksysteemInterests): ZaaksysteemLiveInformationRequestModel {

		return {
			connectionId: resourceId.connectionId,
			resourceKind: resourceId.resourceKind,
			interestedIn: interestedIn
		};
	}

	private convertResponseToResourceId(response: ZaaksysteemLiveInformationResponseModel): ZaaksysteemResourceId {
		return {
			connectionId: response.connectionId,
			resourceKind: response.resourceKind
		};
	}
}

function normalizeKey(key: ZaaksysteemResourceId): string {
	let normalizedKey: string = `connection_${key.connectionId}`;
	switch (key.resourceKind) {
		case ZaaksysteemResourceKind.MyOpenCases:
			normalizedKey += "_MyOpenCases";
			break;
		case ZaaksysteemResourceKind.MySuspendedCases:
			normalizedKey += "_MySuspendedCases";
			break;
		default:
			throw new UnreachableCaseError(key.resourceKind);
	}

	return normalizedKey;
}

export {
	type InterestedZaaksysteemParty,
	type ZaaksysteemLiveInformationProviderOptions,
	ZaaksysteemLiveInformationProvider,
	type ZaaksysteemResourceId
};